import React, { useState, useEffect, useRef } from 'react';
import './ProjectDetails.css';
import HOASection from './HOASection';
import SummaryModal from './SummaryModal';

import { baseurl } from '../baseurl';
import axios from 'axios';
import Swal from 'sweetalert2';
import * as turf from '@turf/turf';
import SaveModal from './SaveModal';

const ProjectDetails = ({locationName, coords, area, setLocationName, geometry, config, editData, 
  nearmapImage, nearmapVintage
}) => {
    // Dropdown States
    const [isOpen, setIsOpen] = useState(false);
    const [selectedType, setSelectedType] = useState('land+snow');
    const dropdownRef = useRef(null);


    // console.log("geometry geometry", geometry);
    console.log("nearmapImage", nearmapImage);
    console.log("nearmapVintage", nearmapVintage);

    // Dynamic States
    const [reportOptions, setReportOptions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Sitemap & Instructions States
    const [uploadedFile, setUploadedFile] = useState(null); 
    const [instructions, setInstructions] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newInstruction, setNewInstruction] = useState("");

    // UI States
    const [isMinutes, setIsMinutes] = useState(false);
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);

    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isAcres, setIsAcres] = useState(false);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [tempAddress, setTempAddress] = useState(locationName);
    const [propertyId, setPropertyId] = useState("");

    // Area State for Initial Load
    const [initialCalculatedArea, setInitialCalculatedArea] = useState(0);

    // HOA state
    const [hoaSelections, setHoaSelections] = useState({
        commonArea: false,
        excludeCurb: false,
        includeFront: false,
    });

    const userToken = localStorage.getItem('userToken');

    // --- 1. SYNC EDIT DATA ---
    useEffect(() => {
        if (editData && editData.project_data) {
            const data = editData.project_data;

            if (editData.property_id) setPropertyId(editData.property_id);

            if (data.hoaSpecifications) {
                setHoaSelections({
                    commonArea: !!data.hoaSpecifications.commonArea,
                    excludeCurb: !!data.hoaSpecifications.excludeCurb,
                    includeFront: !!data.hoaSpecifications.includeFront,
                });
            }

            if (data.instructions) setInstructions(data.instructions);

            if (data.sitemap_url) {
                setUploadedFile({ name: "Current Sitemap", url: data.sitemap_url });
            }
        } else {
            const randomStr = Math.random().toString(36).substring(2, 10).toUpperCase();
            const timestamp = Date.now().toString(36).toUpperCase();
            setPropertyId(`MES-PR-${randomStr}${timestamp}`);
        }
    }, [editData]);

    // --- 2. API Integration for Packages ---
    useEffect(() => {
        const fetchPackages = async () => {
            try {
                const response = await axios.get(`${baseurl}getAllServicePackagesUser`, {
                    headers: { Authorization: `Bearer ${userToken}` }
                });

                if (response.data.success) {
                    const packages = response.data.data;
                    setReportOptions(packages);

                    const savedReportTypeName = editData?.project_data?.reportType;
                    const defaultSetting = config?.default_report_type;

                    let matchedPackage = packages.find(pkg => 
                        pkg.name === savedReportTypeName || pkg.id === savedReportTypeName
                    );

                    if (!matchedPackage) {
                        matchedPackage = packages.find(pkg => 
                            pkg.name === defaultSetting || pkg.id === defaultSetting
                        );
                    }

                    if (matchedPackage) {
                        setSelectedType(matchedPackage.id);
                    } else if (packages.length > 0) {
                        setSelectedType(packages[0].id);
                    }
                }
            } catch (err) {
                console.error("Error fetching packages:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchPackages();
    }, [config, editData, userToken]);

 
// --- 3. ONLOAD GEOMETRY CALCULATION ---
useEffect(() => {
  const geoToUse = geometry || editData?.geometry;
  if (!geoToUse) return;

  let totalCalculated = 0;

  const features = geoToUse.features
    ? geoToUse.features
    : Array.isArray(geoToUse) ? geoToUse : [geoToUse];

  features.forEach(feature => {
    if (!feature?.geometry) return;
    try {
      const type = feature.geometry.type;

      if (type === 'Polygon' || type === 'MultiPolygon') {
        // Validate coords are [lng, lat] — turf expects lng first
        // Log raw coords to confirm order
        const rawCoords = type === 'Polygon'
          ? feature.geometry.coordinates[0]
          : feature.geometry.coordinates[0][0];

        console.log("=== RAW COORDS SAMPLE (first 3 points) ===", rawCoords?.slice(0, 3));
        console.log("=== Coord format: [0]=lng, [1]=lat — lng should be negative for USA ===");

        // Check if coords are swapped ([lat, lng] instead of [lng, lat])
        // US longitudes are negative (~-70 to -130), latitudes positive (~25 to 50)
        const firstPoint = rawCoords?.[0];
        let correctedFeature = feature;

        if (firstPoint && firstPoint[0] > 0 && firstPoint[0] < 90) {
          // Likely [lat, lng] — swap to [lng, lat] for turf
          console.warn("=== COORDS APPEAR SWAPPED — auto-correcting to [lng, lat] ===");

          const swapRing = ring => ring.map(c => [c[1], c[0]]);

          correctedFeature = {
            ...feature,
            geometry: {
              ...feature.geometry,
              coordinates: type === 'Polygon'
                ? feature.geometry.coordinates.map(swapRing)
                : feature.geometry.coordinates.map(poly => poly.map(swapRing)),
            }
          };
        }

        const areaSqM = turf.area(correctedFeature);
        const areaSqFt = areaSqM * 10.7639;
        const areaAcres = areaSqM / 4046.86;

        console.log("=== AREA CALCULATION ===");
        console.log("Area (sq meters):", areaSqM.toFixed(2));
        console.log("Area (sq feet):", areaSqFt.toFixed(2));
        console.log("Area (acres):", areaAcres.toFixed(4));

        totalCalculated += areaSqM; // store in sqft
      }
    } catch (err) {
      console.error("Error calculating area:", err);
    }
  });

  console.log("=== TOTAL AREA (sqft) ===", totalCalculated.toFixed(2));
  setInitialCalculatedArea(totalCalculated);
}, [editData, geometry]);

    // --- 4. HELPER FUNCTIONS ---
    const currentSelection = reportOptions.find(opt => opt.id === selectedType) || 
                             reportOptions[0] || 
                             { name: "Loading...", description: "", features: [] };

    const handleSaveInstruction = () => {
        if (newInstruction.trim()) {
            setInstructions([...instructions, newInstruction]);
            setNewInstruction("");
            setIsModalOpen(false);
        }
    };



    // Add this near your other useState hooks
const [displayUnit, setDisplayUnit] = useState(config?.measurement_unit || 'sqft');

// Keep displayUnit in sync if config changes externally
useEffect(() => {
    if (config?.measurement_unit) {
        setDisplayUnit(config.measurement_unit);
    }
}, [config?.measurement_unit]);



// No change needed IF you were already converting — but verify:
const getDisplayArea = () => {
  const rawSqM = (parseFloat(area) > 0) ? parseFloat(area) : initialCalculatedArea;

  console.log("=== getDisplayArea — sq meters:", rawSqM, "| unit:", displayUnit);

  if (displayUnit.toLowerCase() === "acres") {
    return (rawSqM / 4046.86).toFixed(2);  // sq meters → acres
  }

  // sq meters → sqft
  const sqft = rawSqM * 10.7639;
  return sqft.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};





    useEffect(() => {
        setTempAddress(locationName);
    }, [locationName]);

    const handleAddressUpdate = () => {
        setLocationName(tempAddress); 
        setIsAddressModalOpen(false);
    };

    const getFormattedTime = () => {
        const numericArea = (parseFloat(area) > 0) ? parseFloat(area) : initialCalculatedArea;
        // const areaInAcres = numericArea / 43560;
        const areaInAcres = numericArea / 4046.86; // sq meters → acres
        
        const baseUnit = parseFloat(currentSelection.base_unit_acre) || 1;
        const timePerUnit = parseFloat(currentSelection.est_time_value) || 0;
        
        const totalTime = (areaInAcres / baseUnit) * timePerUnit;

        if (isMinutes) {
            const mins = currentSelection.est_time_unit === 'secs' ? totalTime / 60 : totalTime;
            return `≈ ${Math.ceil(mins)} Mins`;
        }
        
        const hours = currentSelection.est_time_unit === 'mins' ? totalTime / 60 : totalTime / 3600;
        return `≈ ${hours.toFixed(1)} Hours`;
    };

const getTotalCredits = () => {
    const numericArea = (parseFloat(area) > 0) ? parseFloat(area) : initialCalculatedArea;
    const areaInAcres = numericArea / 4046.86;

    const baseUnit = parseFloat(currentSelection.base_unit_acre) || 1;
    const rate = parseFloat(currentSelection.credit_rate) || 0;

    let total = (areaInAcres / baseUnit) * rate;

    if (hoaSelections.commonArea && hoaSelections.includeFront) {
        total = total * 0.85;
    } else if (hoaSelections.commonArea) {
        total = total * 0.70;
    } else if (hoaSelections.includeFront) {
        total = total * 0.85;
    }

    return total.toFixed(2);
};

    const getDiscountLabel = () => {
    if (hoaSelections.commonArea && hoaSelections.includeFront) return "-15%";
    if (hoaSelections.commonArea) return "-30%";
    if (hoaSelections.includeFront) return "-15%";
    return null;
};

    const toggleHOASelection = (key) => {
        setHoaSelections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    return ( 


    <div className="clean-premium-sidebar">
      {/* HEADER SECTION */}
      <div className="panel-header-modern mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="panel-title-group">
            <i className="fa-solid fa-clipboard-list panel-icon-main" style={{color: "#6a11cb"}}></i>
            <h5 className="m-0 fw-bold">Project Details</h5>
          </div>
          <div className="premium-status-pill">Draft</div>
        </div>
        
        {/* PROPERTY ID STRIP - Correctly positioned below title */}
        <label className="property-id-label">Property ID</label>
        <div className="id-value-display">{propertyId}</div>
        {/* <div className="property-id-strip">
          
          
        </div> */}
      </div>

      {/* PRIMARY INFO CARD (SITE METRICS) */}
      <div className="detail-card-premium mb-3">
        <div className="card-header-sub">
          <div className="d-flex align-items-center gap-2">
            <i className="fa-solid fa-circle-info  " style={{color: "#6a11cb"}}></i>
            <span className="fw-bold text-dark">Project Information</span>
          </div>
        </div>

        {/* Address Row */}
        <div className="detail-row-modern">
  <div className="detail-label-group">
    <i className="fa-solid fa-map-marker-alt"></i>
    <span>Address</span>
  </div>
  <div className="detail-value-group">
    <span className="address-truncate">{locationName}</span>
    <button 
      className="edit-btn-inline" 
      onClick={() => setIsAddressModalOpen(true)}
    >
      <i className="fa-solid fa-pen-to-square"></i>
    </button>
  </div>
</div>


{/* ADDRESS EDIT MODAL */}
{isAddressModalOpen && (
  <div className="instruction-modal-overlay">
    <div className="instruction-modal-content">
      <div className="modal-header">
        <div className="modal-title">
          <i className="fa-solid fa-location-dot me-2"></i> Edit Address
        </div>
        <button className="close-x" onClick={() => setIsAddressModalOpen(false)}>&times;</button>
      </div>
      <div className="modal-body">
        <label className="text-muted small mb-2">Display Address</label>
        <textarea 
          className="instruction-textarea" 
          rows="3"
          value={tempAddress}
          onChange={(e) => setTempAddress(e.target.value)}
        ></textarea>
      </div>
      <div className="modal-footer">
        <button className="btn-cancel" onClick={() => setIsAddressModalOpen(false)}>Cancel</button>
        <button className="btn-save" onClick={handleAddressUpdate}>Update Address</button>
      </div>
    </div>
  </div>
)}



        {/* Lot Area Row */}
        <div className="detail-row-modern">
  <div className="detail-label-group">
    <i className="fa-solid fa-expand-arrows-alt"></i>
    <span>Lot Area</span>
  </div>
  <div className="area-pill">
    <span className="fw-bold">{getDisplayArea()}</span>
    
    {/* Display current local unit */}
    <small className="ms-1 text-capitalize">
      {displayUnit}
    </small>

    <button 
      className="unit-toggle" 
      onClick={() => setDisplayUnit(prev => prev === 'sqft' ? 'acres' : 'sqft')}
      type="button"
      title="Toggle Unit"
    >
      <i className="fa-solid fa-arrows-rotate"></i>
    </button>
  </div>
</div>

        {/* Created Date Row */}
        <div className="detail-row-modern no-border">
          <div className="detail-label-group">
            <i className="fa-regular fa-calendar-check"></i>
            <span>Created</span>
          </div>
          <div className="detail-value-text">{new Date().toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit', 
      year: 'numeric' 
    }).replace(',', '')}</div>
        </div>
      </div>



      {/* Report Type */}
<div className="detail-card"> 

        <div className="d-flex align-items-center gap-2">
            <i className="fas fa-file-alt card-icon" style={{color: "#6a11cb"}}></i>
            <span className="fw-bold text-dark"> Report Type</span>
          </div>


{/* Dropdown Section */}
          <div className="dropdown-container" ref={dropdownRef}>
  <div className="dropdown-header" onClick={() => setIsOpen(!isOpen)}>
    <div className="dropdown-selected">
      <div className="dropdown-selected-name">{currentSelection.name}</div>
      <div className="dropdown-selected-desc">{currentSelection.description}</div>
    </div>
    <div className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>
      <i className="fas fa-chevron-down"></i>
    </div>
  </div>

  {isOpen && (
    <div className="dropdown-list">
      {reportOptions.map((option) => (
        <div 
          key={option.id} 
          className="dropdown-item" 
          onClick={() => {
            setSelectedType(option.id);
            setIsOpen(false);
          }}
        >
          <div className="dropdown-item-name">{option.name}</div>
          <div className="dropdown-item-desc">{option.description}</div>
        </div>
      ))}
    </div>
  )}
</div>

<div className="features-section">
  <div className="features-title">
    <i className="fas fa-list-check" style={{color: "#6a11cb"}}></i>
    Features Covered
  </div>
  <div className="features-list">
    {currentSelection.features?.map((feature, index) => (
      <div key={index} className="feature-static">
        {feature}
      </div>
    ))}
  </div>
</div>
            
      </div>



{/* SITEMAP & INSTRUCTIONS SECTION */}
<div className="sitemap-instructions-container mb-3">
 

<div className="d-flex align-items-center gap-2 mb-2">
           <i className="fas fa-paperclip me-2  " style={{color: "#6a11cb"}}></i>
            <span className="fw-bold text-dark"> Sitemap & Instructions</span>
          </div>


  <div className="action-cards-row">
  {/* Upload Sitemap Card */}
  {!uploadedFile ? (
    <label className="action-card" htmlFor="sitemap-upload">
      <input 
        type="file" 
        id="sitemap-upload" 
        hidden 
        accept=".jpg,.png,.pdf" 
        onChange={(e) => {
          if (e.target.files[0]) {
            setUploadedFile(e.target.files[0]); // Store the full file object
          }
        }}
      />
      <div className="icon-wrapper upload-bg">
        <i className="fas fa-cloud-upload-alt"></i>
      </div>
      <h3>Upload Sitemap</h3>
      <p>Price and TAT updated per sitemap. JPG, PNG, PDF</p>
    </label>
  ) : (
    /* Selected File Display */
    <div className="action-card file-selected-card">
      <div className="icon-wrapper success-bg">
        <i className="fas fa-file-alt"></i>
      </div>
      <div className="file-info-container">
        <h3 className="file-name-text">{uploadedFile.name}</h3>
        <button 
          className="remove-file-btn" 
          onClick={() => setUploadedFile(null)}
        >
          <i className="fas fa-times-circle me-1"></i> Remove
        </button>
      </div>
    </div>
  )}

  {/* Add Instruction Card */}
<div className="action-card" onClick={() => setIsModalOpen(true)}>
  <div className="icon-wrapper edit-bg">
    <i className="fas fa-edit"></i>
  </div>
  <h3>Add Instruction</h3> 
    <p>Provide specific instructions to follow</p>

  {instructions.length > 0 ? (
    <div className="instruction-preview-list">
      {instructions.slice(0, 2).map((inst, index) => (
        <p key={index} className="instruction-preview-item">
          • {inst.substring(0, 30)}{inst.length > 30 ? '...' : ''}
        </p>
      ))}
      {instructions.length > 2 && <p className="more-count">+{instructions.length - 2} more</p>}
    </div>
  ) : (
    <p> </p>
  )}
</div>
</div>
 
</div>

{/* INSTRUCTION MODAL */}
{isModalOpen && (
  <div className="instruction-modal-overlay">
    <div className="instruction-modal-content">
      <div className="modal-header">
        <div className="modal-title">
          <i className="fas fa-edit me-2"></i> Manage Instructions
        </div>
        <button className="close-x" onClick={() => setIsModalOpen(false)}>&times;</button>
      </div>
      
      <div className="modal-body">
        {/* List Existing Instructions */}
        {instructions.length > 0 && (
          <div className="existing-instructions-section">
            <label className="modal-label">Current Instructions</label>
            <div className="existing-list">
              {instructions.map((inst, index) => (
                <div key={index} className="existing-item">
                  <span>{inst}</span>
                  <button 
                    className="delete-inst-btn" 
                    onClick={() => setInstructions(instructions.filter((_, i) => i !== index))}
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add New Instruction */}
        <label className="modal-label">New Instruction</label>
        <textarea 
          className="instruction-textarea" 
          placeholder="Enter your specific instructions here..."
          value={newInstruction}
          onChange={(e) => setNewInstruction(e.target.value)}
        ></textarea>
      </div>

      <div className="modal-footer">
        <button className="btn-cancel" onClick={() => setIsModalOpen(false)}>Close</button>
        <button 
          className="btn-save" 
          onClick={handleSaveInstruction}
          disabled={!newInstruction.trim()}
        >
          Add to List
        </button>
      </div>
    </div>
  </div>
)}



 
<HOASection 
  selections={hoaSelections} 
  toggleSelection={toggleHOASelection} 
/>



{/* Project estimate  */}
{/* PROJECT ESTIMATE SECTION */}
<div className="estimate-card-premium mt-3">
  <div className="d-flex align-items-center gap-2 mb-3">
    <i className="fa-solid fa-calculator text-purple-main"></i>
    <span className="fw-bold text-dark">Project Estimate</span>
  </div>

  {/* Time Required Row */}
  <div className="estimate-row">
  <span className="estimate-label">TIME REQUIRED</span>
  <div className="time-value-group">
    {/* Call the formatting function here */}
    <span className="time-highlight">{getFormattedTime()}</span>
    
    {/* Added onClick to toggle the unit */}
    <button 
      className="sync-btn" 
      onClick={() => setIsMinutes(!isMinutes)}
      title="Toggle Hours/Minutes"
    >
      <i className="fas fa-exchange-alt"></i>
    </button>
  </div>
</div>

  <div className="divider-dashed"></div>

  {/* Estimated Cost Row */}
<div className="estimate-row align-items-center mb-4">
  <span className="estimate-label">ESTIMATED COST</span>
  
  <div className="d-flex flex-column align-items-end">
    <div className="d-flex align-items-center">
      
      {/* Dynamic Discount Badge - Only shows when commonArea is active */}
     
      {/* Credit Display */}
      <span className="cost-value" style={{ color: '#4318FF', fontWeight: 'bold', fontSize: '18px' }}>
        {getTotalCredits()} Credits
      </span>

     {(hoaSelections.commonArea || hoaSelections.includeFront) && (
  <span 
    className="ms-1" 
    style={{ 
      color: '#05CD99',
      fontSize: '11px',
      fontWeight: '500',
      padding: '2px 8px',
      borderRadius: '12px',
      display: 'inline-block'
    }}
  >
    ({getDiscountLabel()})
  </span>
)}


    </div>

    {/* Sub-text showing rate details */}
    {/* <small className="text-muted" style={{ fontSize: '10px' }}>
      ({currentSelection.credit_rate} Cr / {currentSelection.base_unit_acre} Ac)
    </small> */}
  </div>
</div>
 
</div>

{/* FOOTER ACTION BUTTONS */}
<div className="footer-actions-container mt-3" style={{ 
  width: "100%", 
  display: "flex", 
  gap: "10px" // Adds a nice space between buttons
}}> 

  <button 
  className="btn-action-main btn-gen btn-generate" 
  onClick={() => setIsSummaryModalOpen(true)} 
  style={{ flex: 1 }}
>
  {/* Switch icon based on edit mode */}
  <i className={`fa-solid ${editData ? 'fa-pen-to-square' : 'fa-file-arrow-down'}`}></i>
  
  {/* Dynamic Label */}
  <span>{editData ? 'Update Parcel' : 'Generate'}</span>
</button>

<button 
  className="btn-action-outline tn-ccl" 
  style={{ flex: 1 }}
  onClick={() => {
    Swal.fire({
      title: 'Are you sure?',
      text: "Any unsaved changes will be lost!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4318FF',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, cancel it!',
      cancelButtonText: 'No, stay'
    }).then((result) => {
      if (result.isConfirmed) {
        // Reloads the page to clear all local states
        window.location.reload();
      }
    });
  }}
>
  <i className="fa-solid fa-xmark"></i>
  <span>Cancel</span>
</button>

  <button 
    className="btn-action-outline btn-gen" 
     onClick={() => setIsSaveModalOpen(true)} 
  style={{ flex: 1 }}
  >
    <i className="fa-solid fa-floppy-disk"></i>
    <span>Save</span>
  </button> 
</div>

{isSaveModalOpen && (
  <SaveModal  
    onClose={() => setIsSaveModalOpen(false)} 
    isEdit={!!editData} // true if editData exists
    editData ={editData}
    projectId={editData?.id}
    data={{
      propertyId: propertyId,
      address: locationName,
     lotArea: `${(((parseFloat(area) > 0) ? parseFloat(area) : initialCalculatedArea) * 10.7639).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Sqft`,
      status: "Draft",
      reportType: currentSelection.name,
      reportValue: selectedType,
      features: currentSelection.features, 

      // HOA Data
      hoaSpecifications: hoaSelections, 

      // Instructions & File Data
      instructions: instructions,
      currentDraftNote: newInstruction,
      
      // SENDING THE ACTUAL FILE OBJECT
      // This sends the binary file for uploading or previewing via URL.createObjectURL()
      sitemapFile: uploadedFile, 

      // Map & Geography
      coords: coords, 
      geometry: geometry,

      // Calculations
      estimatedTime: getFormattedTime(),
      totalCredits: getTotalCredits(),
      // Final Total (Apply logic here if 1 credit = $1.00)
      total: (getTotalCredits() * 1).toLocaleString(undefined, { 
        style: 'currency', 
        currency: 'USD' 
      })
    }}
  />
)}



{isSummaryModalOpen && (
  <SummaryModal  
    onClose={() => setIsSummaryModalOpen(false)} 
    isEdit={!!editData} // true if editData exists
    editData ={editData}
    projectId={editData?.id}
    nearmapImage={nearmapImage}      
    nearmapVintage={nearmapVintage}
    data={{
      propertyId: propertyId,
      address: locationName,
      lotArea: `${(((parseFloat(area) > 0) ? parseFloat(area) : initialCalculatedArea) * 10.7639).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Sqft`,
      status: "Draft",
      reportType: currentSelection.name,
      reportValue: selectedType,
      features: currentSelection.features, 

      // HOA Data
      hoaSpecifications: hoaSelections, 

      // Instructions & File Data
      instructions: instructions,
      currentDraftNote: newInstruction,
      
      // SENDING THE ACTUAL FILE OBJECT
      // This sends the binary file for uploading or previewing via URL.createObjectURL()
      sitemapFile: uploadedFile, 

      // Map & Geography
      coords: coords, 
      geometry: geometry, 
      // Calculations
      estimatedTime: getFormattedTime(),
      totalCredits: getTotalCredits(),
      // Final Total (Apply logic here if 1 credit = $1.00)
      total: (getTotalCredits() * 1).toLocaleString(undefined, { 
        style: 'currency', 
        currency: 'USD' 
      })
    }}
  />
)}
  


    </div>
  );
};

export default ProjectDetails;